import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useCart } from "../App";
import { useSuburb } from "../App";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ShoppingCart, User, MapPin, Menu, X, Package, LogOut, Crown, Shield } from "lucide-react";

const Header = ({ openAuthModal }) => {
  const { user, logout, isAuthenticated, isMember, token } = useAuth();
  const { itemCount, setCartOpen } = useCart();
  const { suburb, clearSuburb } = useSuburb();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  // Check if user has founder access
  useEffect(() => {
    const checkFounderAccess = async () => {
      if (!token) {
        setUserRole(null);
        return;
      }
      try {
        const response = await axios.get(`${API}/founders/check`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserRole(response.data);
      } catch {
        setUserRole({ is_founder: false, is_driver: false });
      }
    };
    checkFounderAccess();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200" data-testid="header">
      <div className="container-main">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Outfit']">BundleCart</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {suburb && (
              <button
                onClick={clearSuburb}
                className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600 transition-colors"
                data-testid="change-suburb-btn"
              >
                <MapPin className="w-4 h-4" />
                <span>{suburb}</span>
                <X className="w-3 h-3" />
              </button>
            )}
            <Link
              to="/products"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-products"
            >
              Products
            </Link>
            {isAuthenticated && (
              <Link
                to="/orders"
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="nav-orders"
              >
                Orders
              </Link>
            )}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Member Badge */}
            {isAuthenticated && isMember && (
              <div className="hidden sm:flex items-center gap-1 member-badge" data-testid="member-badge">
                <Crown className="w-3 h-3" />
                <span>Member</span>
              </div>
            )}

            {/* Cart Button */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setCartOpen(true)}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="notification-badge" data-testid="cart-count">{itemCount}</span>
              )}
            </Button>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                    <User className="w-4 h-4 mr-2" />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                    <Package className="w-4 h-4 mr-2" />
                    Orders
                  </DropdownMenuItem>
                  {/* Founder Portal - Only show for founders */}
                  {userRole?.is_founder && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/founders")} data-testid="menu-founder-portal">
                        <Shield className="w-4 h-4 mr-2 text-sky-600" />
                        Founder Portal
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openAuthModal("login")}
                  data-testid="login-btn"
                >
                  Login
                </Button>
                <Button
                  size="sm"
                  className="btn-accent"
                  onClick={() => openAuthModal("register")}
                  data-testid="signup-btn"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 animate-fade-in">
            <nav className="flex flex-col gap-2">
              {suburb && (
                <button
                  onClick={() => {
                    clearSuburb();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <MapPin className="w-4 h-4" />
                  <span>{suburb}</span>
                  <X className="w-3 h-3 ml-auto" />
                </button>
              )}
              <Link
                to="/products"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Products
              </Link>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/orders"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    Orders
                  </Link>
                  <Link
                    to="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    Account
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      openAuthModal("login");
                      setMobileMenuOpen(false);
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 btn-accent"
                    onClick={() => {
                      openAuthModal("register");
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
